import { Autocomplete, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import useGetProducts from "@/hooks/requests/product/use-get-products";
import IProduct from "@/interfaces/product.interface";
import { ItemDataTabMessages } from "@/messages/order-form-page/item-data-tab.messages";
import { FilterType } from "@/types/filter.types";

import { productHasStock } from "./use-product-skus";

interface Props {
  messages: ItemDataTabMessages;
  value: IProduct | null;
  onChange: (product: IProduct | null) => void;
  errorText?: string | null;
}

const PAGE = { pageNumber: 0, pageSize: 15 };
const DEBOUNCE_DELAY = 250;
const MIN_SEARCH_LENGTH = 2;
const ACTIVE_PRODUCT_FILTER = {
  property: "active" as const,
  filterType: FilterType.Equals,
  value: true,
};

const ProductAutocomplete: React.FC<Props> = ({ messages, value, onChange, errorText }) => {
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const onSearch = useCallback((value: string) => {
    setInputValue(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSearch(value.trim()), DEBOUNCE_DELAY);
  }, []);

  const hasSearch = search.length >= MIN_SEARCH_LENGTH;
  const isDebouncing = inputValue.trim() !== search;

  // O backend não aceita a propriedade "all"; buscamos por título e por código separadamente.
  const { data: byTitle, isLoading: isLoadingTitle } = useGetProducts({
    ...PAGE,
    filters: [
      { property: "title", filterType: FilterType.Contains, value: search },
      ACTIVE_PRODUCT_FILTER,
    ],
    enabled: hasSearch,
  });

  const { data: byCode, isLoading: isLoadingCode } = useGetProducts({
    ...PAGE,
    filters: [
      { property: "code", filterType: FilterType.Contains, value: search },
      ACTIVE_PRODUCT_FILTER,
    ],
    enabled: hasSearch,
  });

  const isLoading =
    (isDebouncing && inputValue.trim().length >= MIN_SEARCH_LENGTH) ||
    (hasSearch && (isLoadingTitle || isLoadingCode));

  const options = useMemo<IProduct[]>(() => {
    if (!hasSearch) return [];

    const merged: IProduct[] = [];
    const seen = new Set<number>();

    [...(byTitle?.items || []), ...(byCode?.items || [])].forEach((product) => {
      if (!product || product.active === false || seen.has(product.id)) return;
      seen.add(product.id);
      merged.push(product);
    });

    return merged;
  }, [byTitle, byCode, hasSearch]);

  return (
    <Autocomplete
      fullWidth
      value={value}
      onChange={(_, selected) => onChange(selected)}
      loading={isLoading}
      loadingText={messages.loadingText}
      noOptionsText={messages.noOptionsItems}
      options={options}
      filterOptions={(items) => items}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => option.title || ""}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <MDBox display="flex" width="100%" gap={1.5} alignItems="center" minWidth={0}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ minWidth: "7rem" }} noWrap>
              {option.code}
            </MDTypography>
            <MDTypography variant="caption" sx={{ minWidth: 0, flex: 1, whiteSpace: "normal" }}>
              {option.title}
            </MDTypography>
            {!productHasStock(option) && (
              <MDTypography variant="caption" color="error" fontWeight="medium" noWrap>
                {messages.outOfStockLabel}
              </MDTypography>
            )}
          </MDBox>
        </li>
      )}
      renderInput={(params) => (
        <MDInput
          {...params}
          variant="outlined"
          label={messages.productSearchLabel}
          error={!!errorText}
          helperText={errorText || (value && !productHasStock(value) ? messages.outOfStockLabel : "")}
          FormHelperTextProps={{ sx: { color: "error.main" } }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProductAutocomplete;
